import React, { useState, useEffect } from "react";

const TYPE_OPTIONS = ["To Do", "Someday", "Reminder"];
const TAG_OPTIONS = ["Email", "Call", "Pay", "Schedule", "Sign Up"];
const PRIORITY_OPTIONS = ["High", "Medium", "Low"];
const PERSON_OPTIONS = ["Adam", "Kimberly", "Family", "Carissa", "Robert"];
const STATUS_OPTIONS = ["Not started", "In progress", "Done"];

async function updateTask(pageId, action, value) {
  const res = await fetch("/.netlify/functions/notion", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pageId, action, value }),
  });
  return res.ok;
}

async function deleteTask(pageId) {
  const res = await fetch("/.netlify/functions/notion", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pageId, action: "delete" }),
  });
  return res.ok;
}

export default function EditSheet({ item, onClose, onSave, onDelete }) {
  const [form, setForm] = useState({
    name: "",
    dueDate: "",
    priority: "Medium",
    type: "To Do",
    tag: [],
    person: "",
    status: "Not started",
  });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [description, setDescription] = useState("");
  const [origDescription, setOrigDescription] = useState(null); // null = not loaded yet
  const [descLoading, setDescLoading] = useState(false);

  // Load the Notion page body as a plain-text description (read-only)
  useEffect(() => {
    if (!item?.id) { setDescription(""); setOrigDescription(null); return; }
    let cancelled = false;
    setDescription("");
    setOrigDescription(null);
    setDescLoading(true);
    fetch(`/.netlify/functions/notion?type=blocks&pageId=${item.id}`)
      .then(r => r.json())
      .then(d => { if (!cancelled && Array.isArray(d.blocks)) { const t = d.blocks.map(b => b.text).join("\n"); setDescription(t); setOrigDescription(t); } })
      .catch(() => {})
      .finally(() => { if (!cancelled) setDescLoading(false); });
    return () => { cancelled = true; };
  }, [item?.id]);

  useEffect(() => {
    if (item) {
      setForm({
        name: item.name || "",
        dueDate: item.dueDate || "",
        priority: item.priority || "Medium",
        type: item.type || "To Do",
        tag: item.tag || [],
        person: item.person || "",
        status: item.status || "Not started",
      });
      setConfirmDelete(false);
    }
  }, [item]);

  if (!item) return null;

  const toggleTag = (t) => {
    setForm(prev => {
      const has = prev.tag.includes(t);
      return { ...prev, tag: has ? prev.tag.filter(x => x !== t) : [...prev.tag, t] };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    await updateTask(item.id, "updateFields", form);
    if (origDescription !== null && description !== origDescription) {
      await updateTask(item.id, "updateTaskBody", { text: description });
    }
    setSaving(false);
    onSave({ ...item, ...form, isHighPriority: form.priority === "High" });
  };

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setSaving(true);
    await deleteTask(item.id);
    setSaving(false);
    onDelete(item.id);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 200,
          animation: "fadeIn 0.2s ease",
        }}
      />

      {/* Sheet */}
      <div style={{
        position: "fixed",
        bottom: 0, left: "50%",
        transform: "translateX(-50%)",
        width: "100%", maxWidth: "var(--app-max-width)",
        background: "var(--white)",
        borderRadius: "20px 20px 0 0",
        zIndex: 201,
        animation: "slideUp 0.25s ease",
        paddingBottom: "env(safe-area-inset-bottom, 20px)",
      }}>

        {/* Handle */}
        <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: "var(--border)" }} />
        </div>

        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "8px 20px 16px",
          borderBottom: "1px solid var(--border)",
        }}>
          <button onClick={onClose} style={{
            background: "none", border: "none", fontSize: 14,
            fontWeight: 700, color: "var(--muted)", cursor: "pointer",
            fontFamily: "inherit",
          }}>Cancel</button>
          <span style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 16, fontWeight: 600, color: "var(--charcoal)",
          }}>Edit Task</span>
          <button onClick={handleSave} disabled={saving} style={{
            background: "none", border: "none", fontSize: 14,
            fontWeight: 800, color: saving ? "var(--muted)" : "var(--sage)",
            cursor: saving ? "default" : "pointer",
            fontFamily: "inherit",
          }}>{saving ? "Saving…" : "Save"}</button>
        </div>

        {/* Fields */}
        <div style={{ padding: "8px 0", maxHeight: "60vh", overflowY: "auto" }}>

          {/* Name */}
          <div style={{ padding: "10px 20px", borderBottom: "1px solid var(--border)" }}>
            <div style={fieldLabel}>Name</div>
            <textarea
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              rows={2}
              style={{
                width: "100%", border: "none", outline: "none",
                fontSize: 16, fontWeight: 500, color: "var(--charcoal)",
                fontFamily: "inherit", resize: "none",
                background: "transparent", marginTop: 4,
              }}
            />
          </div>


          {/* Description (Notion page body, plain text) */}
          <div style={{ padding: "10px 20px", borderBottom: "1px solid var(--border)" }}>
            <div style={fieldLabel}>Description</div>
            <textarea
              value={origDescription === null ? "" : description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={origDescription === null}
              placeholder={origDescription === null ? "Loading…" : "Add details"}
              rows={description.split("\n").length > 3 ? 6 : 3}
              style={{
                width: "100%", border: "none", outline: "none",
                fontSize: 14, color: "var(--charcoal)", lineHeight: 1.45,
                fontFamily: "inherit", resize: "vertical",
                background: "transparent", marginTop: 4,
              }}
            />
          </div>

          {/* Due Date */}
          <div style={{ padding: "10px 20px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={fieldLabel}>Due Date</div>
            <input
              type="date"
              value={form.dueDate}
              onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
              style={{
                border: "none", outline: "none", fontSize: 14,
                fontWeight: 600, color: "var(--charcoal)",
                fontFamily: "inherit", background: "transparent",
              }}
            />
          </div>

          {/* Priority */}
          <div style={{ padding: "10px 20px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={fieldLabel}>Priority</div>
            <div style={{ display: "flex", gap: 6 }}>
              {PRIORITY_OPTIONS.map((p) => (
                <button key={p} onClick={() => setForm({ ...form, priority: p })} style={{
                  padding: "4px 12px", borderRadius: 20,
                  border: "1.5px solid",
                  borderColor: form.priority === p ? "var(--sage)" : "var(--border)",
                  background: form.priority === p ? "var(--sage)" : "transparent",
                  color: form.priority === p ? "white" : "var(--muted)",
                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                  fontFamily: "inherit",
                }}>{p}</button>
              ))}
            </div>
          </div>

          {/* Type */}
          <div style={{ padding: "10px 20px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={fieldLabel}>Type</div>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              style={selectStyle}
            >
              {TYPE_OPTIONS.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>

          {/* Tag */}
          <div style={{ padding: "10px 20px", borderBottom: "1px solid var(--border)" }}>
            <div style={fieldLabel}>Tag</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
              {TAG_OPTIONS.map((t) => {
                const checked = form.tag.includes(t);
                return (
                  <button key={t} onClick={() => toggleTag(t)} style={{
                    padding: "4px 12px", borderRadius: 20, border: "1.5px solid",
                    borderColor: checked ? "var(--sage)" : "var(--border)",
                    background: checked ? "var(--sage)" : "transparent",
                    color: checked ? "white" : "var(--muted)",
                    fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                  }}>{t}</button>
                );
              })}
            </div>
          </div>

          {/* Person */}
          <div style={{ padding: "10px 20px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={fieldLabel}>Person</div>
            <select
              value={form.person}
              onChange={(e) => setForm({ ...form, person: e.target.value })}
              style={selectStyle}
            >
              <option value="">None</option>
              {PERSON_OPTIONS.map((p) => <option key={p}>{p}</option>)}
            </select>
          </div>

          {/* Status */}
          <div style={{ padding: "10px 20px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={fieldLabel}>Status</div>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              style={selectStyle}
            >
              {STATUS_OPTIONS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>

          {/* Delete */}
          <div style={{ padding: "16px 20px" }}>
            <button
              onClick={handleDelete}
              disabled={saving}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: 12,
                border: "1.5px solid",
                borderColor: confirmDelete ? "var(--rose)" : "var(--border)",
                background: confirmDelete ? "var(--rose)" : "transparent",
                color: confirmDelete ? "white" : "var(--rose)",
                fontSize: 14, fontWeight: 700,
                cursor: saving ? "default" : "pointer",
                fontFamily: "inherit",
              }}
            >
              {confirmDelete ? "Tap again to confirm delete" : "Delete Task"}
            </button>
          </div>

        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateX(-50%) translateY(100%); }
          to   { transform: translateX(-50%) translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </>
  );
}

const fieldLabel = {
  fontSize: 11, fontWeight: 800,
  letterSpacing: "0.07em", textTransform: "uppercase",
  color: "var(--muted)",
};

const selectStyle = {
  border: "none", outline: "none",
  fontSize: 14, fontWeight: 600,
  color: "var(--charcoal)",
  fontFamily: "inherit",
  background: "transparent",
  cursor: "pointer",
};
